/**
 * RealKnowledgeGateway — the production adapter that satisfies
 * {@link KnowledgeGateway} by delegating to the Spec Studio client
 * (`api.knowledge`, a {@link KnowledgeApi}).
 *
 * Most Knowledge operations return `{ statusCode, body }` where `body` is a JSON
 * string; this adapter parses those strings and maps the raw Spec Studio shapes
 * onto the feature's normalized domain types. The exception is `getSpecDoc`,
 * whose `body` is the RAW document content (markdown or a JSON string), used
 * verbatim. Parsing is defensive: the backend JSON is treated as `unknown` and
 * narrowed field-by-field so a schema drift degrades gracefully rather than
 * throwing.
 *
 * Strict layering: this module talks ONLY to the injected `api.knowledge`
 * client (never `fetch`, never `ApiClient`). Pure TS — no React.
 */

import type { KnowledgeApi } from '../../api';
import type { KnowledgeResponse } from '../../api';
import type {
  KnowledgeGateway,
  SemanticSearchOptions,
} from './knowledgeGateway';
import type {
  ComplexityLevel,
  EffortInsight,
  EffortRange,
  KnowledgeCollection,
  KnowledgePackage,
  KnowledgeRevision,
  SemanticSearchHit,
  SpecDocKind,
  SpecDocument,
  SpecDocumentContent,
} from './types';

// --- tiny defensive JSON narrowing helpers ---------------------------------

type Json = Record<string, unknown>;

function parse(res: KnowledgeResponse): unknown {
  try {
    return JSON.parse(res.body);
  } catch {
    return undefined;
  }
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    // Common envelope shapes: { items: [...] } / { results: [...] }.
    const o = value as Json;
    for (const key of [
      'items',
      'results',
      'packages',
      'collections',
      'revisions',
      'documents',
      'hits',
    ]) {
      if (Array.isArray(o[key])) return o[key] as unknown[];
    }
  }
  return [];
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Narrows a value to an array of non-empty strings (drops non-string entries). */
function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((v) => str(v)).filter((s): s is string => Boolean(s))
    : [];
}

function obj(value: unknown): Json {
  return value && typeof value === 'object' ? (value as Json) : {};
}

function pick(o: Json, ...keys: string[]): unknown {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
}

const DOC_KINDS: SpecDocKind[] = [
  'overview',
  'requirements',
  'architecture',
  'api',
  'data-model',
  'analysis',
  'other',
];

function classifyDoc(path: string, title: string): SpecDocKind {
  const hay = `${path} ${title}`.toLowerCase();
  const found = DOC_KINDS.find(
    (k) => hay.includes(k.replace('-', ' ')) || hay.includes(k),
  );
  if (found) return found;
  if (hay.includes('overview')) return 'overview';
  if (hay.includes('requirement') || hay.includes('specification'))
    return 'requirements';
  return 'other';
}

// --- mappers ---------------------------------------------------------------

function mapPackage(raw: unknown): KnowledgePackage {
  const o = obj(raw);
  const rev = obj(pick(o, 'latestRevision', 'revision'));
  return {
    id: str(pick(o, 'id', 'packageId', 'name')) ?? '',
    name: str(pick(o, 'name', 'packageName')) ?? '',
    description: str(pick(o, 'description', 'summary', 'searchSummary')),
    language: str(pick(o, 'language', 'lang')),
    team: str(pick(o, 'team', 'teamId', 'owner', 'requestor')),
    latestRevisionId:
      str(pick(o, 'latestRevisionId', 'revisionId')) ?? str(rev.revisionId),
    revisionCount: num(pick(o, 'revisionCount', 'targetPackageCount')),
    collectionIds: Array.isArray(o.collections)
      ? (o.collections as unknown[])
          .map((c) => str(obj(c).id) ?? str(c) ?? '')
          .filter(Boolean)
      : undefined,
    updatedAt: str(pick(o, 'updatedAt', 'createdAt', 'createdDate')),
    specType: str(pick(o, 'specType', 'type')) === 'V1' ? 'V1' : 'V2',
  };
}

function mapRevision(packageName: string, raw: unknown): KnowledgeRevision {
  const o = obj(raw);
  const status = str(pick(o, 'status')) ?? 'SUCCESS';
  const normStatus: KnowledgeRevision['status'] =
    status === 'PARTIAL_SUCCESS' || status === 'FAILED' || status === 'PENDING'
      ? status
      : 'SUCCESS';
  return {
    packageName,
    revisionId: str(pick(o, 'revisionId', 'id')) ?? '',
    status: normStatus,
    createdAt: str(pick(o, 'createdAt', 'createdDate')) ?? '',
    createdBy: str(pick(o, 'createdBy', 'creator')),
    isLatest: Boolean(pick(o, 'isLatest', 'latest')),
  };
}

/**
 * Derives a human-readable title from an artifact path, e.g.
 * "docs/system-overview.md" -> "System Overview",
 * "analysis/api-contracts.json" -> "Api Contracts".
 */
function titleFromPath(path: string): string {
  const base = path.split('/').pop() ?? path;
  const stem = base.replace(/\.[^.]+$/, '');
  const words = stem.replace(/[-_]+/g, ' ').trim();
  return words ? words.replace(/\b\w/g, (c) => c.toUpperCase()) : path;
}

/** Maps a single artifact-path string to a normalized {@link SpecDocument}. */
function mapDocumentFromPath(path: string): SpecDocument {
  const title = titleFromPath(path);
  return { path, title, kind: classifyDoc(path, title) };
}

/**
 * Collects the artifact-path strings that make up a revision's document list:
 * the `documentationS3Path` entries, plus any `analysis/*` artifact paths the
 * revision exposes on other fields. Feature-source files and other arrays are
 * intentionally excluded. The revision object is the raw getSpecPackageDocuments
 * / getRevisionById payload (or an unwrapped getLatestRevision `revision`).
 */
function revisionDocumentPaths(rev: Json): string[] {
  const seen = new Set<string>();
  const paths: string[] = [];
  const add = (p: string) => {
    if (p && !seen.has(p)) {
      seen.add(p);
      paths.push(p);
    }
  };
  // Primary document list — the documentation artifact paths.
  for (const p of asStringArray(pick(rev, 'documentationS3Path'))) add(p);
  // Plus any analysis artifact paths, wherever they surface on the revision.
  for (const value of Object.values(rev)) {
    for (const p of asStringArray(value)) {
      if (p.startsWith('analysis/')) add(p);
    }
  }
  return paths;
}

function mapHit(raw: unknown): SemanticSearchHit {
  const o = obj(raw);
  // Real results carry identity fields on a nested `metadata` object.
  const meta = obj(pick(o, 'metadata', 'meta'));
  const path =
    str(pick(o, 'artifactPath', 'path')) ??
    str(pick(meta, 'artifactPath', 'path', 'source'));
  const title =
    str(pick(o, 'title', 'name')) ??
    str(pick(meta, 'title', 'name')) ??
    path ??
    '';
  return {
    packageName:
      str(pick(o, 'packageName', 'package')) ??
      str(pick(meta, 'packageName', 'package')) ??
      '',
    revisionId: str(pick(o, 'revisionId')) ?? str(pick(meta, 'revisionId')),
    path,
    title,
    snippet: str(pick(o, 'snippet', 'content', 'text')) ?? '',
    score: num(pick(o, 'score', 'relevance')) ?? 0,
  };
}

function mapCollection(raw: unknown): KnowledgeCollection {
  const o = obj(raw);
  return {
    id: str(pick(o, 'id', 'collectionId')) ?? '',
    name: str(pick(o, 'name')) ?? '',
    description: str(pick(o, 'description', 'summary', 'searchSummary')),
    latestRevisionId: str(pick(o, 'latestRevisionId', 'collectionRevisionId')),
    packageNames: Array.isArray(o.packageNames)
      ? (o.packageNames as unknown[]).map((n) => str(n) ?? '').filter(Boolean)
      : Array.isArray(o.elements)
        ? (o.elements as unknown[])
            .map((e) => str(obj(e).name) ?? '')
            .filter(Boolean)
        : undefined,
  };
}

// --- effort derivation ------------------------------------------------------

const COMPLEXITY_SCORE: Record<ComplexityLevel, number> = {
  low: 30,
  medium: 55,
  high: 75,
  'very-high': 90,
};

/**
 * The real backend has no dedicated "effort" operation, so this derives a
 * best-effort insight from the package's document set (kinds + counts). It is
 * intentionally coarse; when a richer analysis endpoint exists it can replace
 * this heuristic without changing the gateway contract.
 */
function deriveInsight(
  packageName: string,
  revisionId: string | undefined,
  docs: SpecDocument[],
): EffortInsight {
  const analysisDocs = docs.filter((d) => d.kind === 'analysis').length;
  const apiDocs = docs.filter((d) => d.kind === 'api').length;
  const total = docs.length;

  const complexity: ComplexityLevel =
    total >= 8
      ? 'very-high'
      : total >= 5
        ? 'high'
        : total >= 3
          ? 'medium'
          : 'low';
  const score = COMPLEXITY_SCORE[complexity];
  const base = Math.max(4, total * 2);
  const effort: EffortRange = {
    optimistic: base,
    likely: Math.round(base * 1.5),
    pessimistic: Math.round(base * 2.4),
  };

  return {
    packageName,
    revisionId,
    summary:
      'Heuristic effort estimate derived from the package spec document set. ' +
      'Replace with a dedicated analysis endpoint when available.',
    overallComplexity: complexity,
    complexityScore: score,
    estimatedEffortDays: effort,
    components: docs.slice(0, 5).map((d) => ({
      name: d.title,
      path: d.path,
      complexity: d.kind === 'analysis' ? 'high' : 'medium',
      description: d.summary ?? d.title,
      estimatedDays: Math.max(2, Math.round(effort.likely / Math.max(1, total))),
    })),
    signals: [
      { label: 'Spec documents', value: String(total), tone: 'neutral' },
      {
        label: 'Analysis documents',
        value: String(analysisDocs),
        tone: analysisDocs ? 'info' : 'warning',
      },
      { label: 'API-contract documents', value: String(apiDocs), tone: 'neutral' },
    ],
    suggestedInputs: [
      {
        key: 'backendServices',
        label: 'Backend services',
        value: Math.max(1, Math.round(total / 2)),
        rationale: 'Approximated from document count.',
      },
      {
        key: 'complexityFactor',
        label: 'Complexity factor',
        value: score / 50,
        rationale: `Derived from ${complexity} complexity band.`,
      },
    ],
    generatedAt: new Date().toISOString(),
  };
}

// --- gateway ----------------------------------------------------------------

export class RealKnowledgeGateway implements KnowledgeGateway {
  readonly mode = 'REAL' as const;

  constructor(private readonly client: KnowledgeApi) {}

  async listPackages(search?: string): Promise<KnowledgePackage[]> {
    const res = await this.client.listPackages(search ? { search } : {});
    return asArray(parse(res))
      .map(mapPackage)
      .filter((p) => p.name);
  }

  async getPackage(packageName: string): Promise<KnowledgePackage | undefined> {
    const res = await this.client.getPackageByName({
      packageName,
      includeRevision: true,
    });
    const parsed = parse(res);
    if (!parsed) return undefined;
    const pkg = mapPackage(parsed);
    return pkg.name ? pkg : undefined;
  }

  async listRevisions(packageName: string): Promise<KnowledgeRevision[]> {
    const res = await this.client.getPackageRevisions({ packageName });
    return asArray(parse(res)).map((r) => mapRevision(packageName, r));
  }

  async listDocuments(
    packageName: string,
    revisionId?: string,
  ): Promise<SpecDocument[]> {
    const res = await this.client.getSpecPackageDocuments({
      packageName,
      revisionId,
    });
    const parsed = parse(res);
    // getSpecPackageDocuments returns the revision object directly (getLatest
    // Revision wraps it under `revision`); the document list is derived from its
    // `documentationS3Path` (+ analysis) artifact-path arrays.
    const rev = obj(pick(obj(parsed), 'revision') ?? parsed);
    return revisionDocumentPaths(rev).map(mapDocumentFromPath);
  }

  async getDocument(
    packageName: string,
    path: string,
    revisionId?: string,
  ): Promise<SpecDocumentContent | undefined> {
    const res = await this.client.getSpecDoc({
      packageName,
      artifactPath: path,
      revisionId,
    });
    // getSpecDoc returns the RAW document content (markdown or a JSON string) as
    // the response body — used verbatim, not JSON-parsed.
    return { path, title: titleFromPath(path), markdown: res.body ?? '' };
  }

  async semanticSearch(
    query: string,
    options?: SemanticSearchOptions,
  ): Promise<SemanticSearchHit[]> {
    const res = await this.client.semanticSearch({
      query,
      packageName: options?.packageName,
      revisionId: options?.revisionId,
      numberOfResults: options?.numberOfResults,
    });
    return asArray(parse(res))
      .map(mapHit)
      .sort((a, b) => b.score - a.score);
  }

  async listCollections(search?: string): Promise<KnowledgeCollection[]> {
    const res = await this.client.listCollections(search ? { search } : {});
    return asArray(parse(res))
      .map(mapCollection)
      .filter((c) => c.id);
  }

  async getEffortInsight(
    packageName: string,
    revisionId?: string,
  ): Promise<EffortInsight | undefined> {
    // No dedicated backend op — derive from the document set (best-effort).
    const docs = await this.listDocuments(packageName, revisionId);
    return deriveInsight(packageName, revisionId, docs);
  }
}
